import { usePathname } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DEFAULT_MONTH_OPTION, DEFAULT_SELECT_MODEL_CATEGORY, INTEREST_TYPE_TIL, MAX_PERCENT, MIN_PERCENT, type ModelCategory, MONTH_OPTIONS, QUERY_MODEL_CATEGORY, TIL_DEFAULT_PERCENT } from '@/app/constants/calculator';
import { computePaymentStringFromPercent, computePercentStringFromPayment, filterModelCategoriesByUid, getModelCategoryForLcvModel, getSubCategoryForLcvModel, isCalculatorQueryClassParam, isQueryModelEqualToDefaultClass, pickModelCategoryPreferNonEuro4, resolveCalculatorModelQueryWithFallbacks, resolveMonthFromQueryTerm } from '@/app/lcv/components/sections/calculator/calculator-helpers';
import { getCategoryKey, getMinTilDownPaymentPercent, getModelSubCategoryListFromCategory, getTilInterestByModel, parseFloatAndHandleNaN, resolveDefaultGradeUid } from '@/app/lcv/components/sections/calculator/utility';
import { createModelQueryParam, getLcvModelColorId } from '@/app/lcv/components/sections/compare/utils';
import type { InterestType, Month } from '@/app/lcv/types/calculator';
import type { LCVTilInterest } from '@/app/types/models/lcv';
import { buildCategoryModelKey, getLcvModelColors, getLcvModelDefaultColor } from '@/services/lineup-model/lcv-product-compare/lcv-models-helpers';
import type { CategoryDefaultGrades, LcvModelWithFieldKey } from '@/services/lineup-model/lcv-product-compare/types';
import { trackLCVCalculator } from '@/tag-manager';
import type { LcvModelCategory, LcvModelColor, LcvModelSubCategory } from '@/types/tis-cms/types';
type CalculatorView = 'preview' | 'result';
export interface CalculatorHomeProps {
  modelYear: string;
  modelCategories: LcvModelCategory[];
  defaultModelCategory: LcvModelCategory;
  defaultModelSubCategory: LcvModelSubCategory;
  defaultSelectedModel: LcvModelWithFieldKey;
  defaultPercent: string;
  defaultSelectedModelPayment: string;
  defaultSelectedModelPrice: string;
  allModels: {
    [key: string]: LcvModelWithFieldKey[];
  };
  defaultGradesByCategoryKey: Record<string, CategoryDefaultGrades>;
  tilVehicleModel: {
    [key: string]: LCVTilInterest[];
  };
  provinces: [];
  /** From `getSpecialCampaign`. */
  isSpecialCampaignOn?: boolean;
}

/**
 * Owns all calculator state, URL sync, and business logic.
 * See calculator-skills.md for the full design and bug history.
 */
export const useCalculatorHome = ({
  modelYear,
  modelCategories,
  defaultModelCategory,
  defaultModelSubCategory,
  defaultSelectedModel,
  defaultPercent,
  defaultSelectedModelPayment,
  defaultSelectedModelPrice,
  allModels,
  defaultGradesByCategoryKey,
  tilVehicleModel
}: CalculatorHomeProps) => {
  const pathname = usePathname();
  const isDefaultInit = useRef(true);
  // Skip color reset in the `[model]` effect when documentId is unchanged.
  const lastReconciledModelUidRef = useRef<string>(defaultSelectedModel?.documentId);
  // Color the link resolved for a specific model; the `[model]` effect re-applies it once
  // for that model, avoiding a stale-render replay bug (see calculator-skills.md §7).
  const initColorForModelRef = useRef<{
    modelDocId: string;
    color: LcvModelColor;
  } | null>(null);
  const [view, setView] = useState<CalculatorView>('preview');
  const [modelCategory, setModelCategory] = useState<LcvModelCategory>(defaultModelCategory);
  const [listModelSubCategory, setListModelSubCategory] = useState<LcvModelSubCategory[] | null>(null);
  const [selectedModelCategoryListItems, setSelectedModelCategoryListItems] = useState<LcvModelWithFieldKey[]>(allModels[buildCategoryModelKey(modelCategory.documentId, modelYear)] || []);
  const [modelSubCategory, setModelSubCategory] = useState<LcvModelSubCategory | null>(defaultModelSubCategory);
  const [model, setModel] = useState<LcvModelWithFieldKey>(defaultSelectedModel);
  const [listColor, setListColor] = useState<LcvModelColor[]>(getLcvModelColors(defaultSelectedModel));
  const [color, setColor] = useState<LcvModelColor>(getLcvModelDefaultColor(defaultSelectedModel)!);
  const [percent, setPercent] = useState<string>(defaultPercent);
  const [payment, setPayment] = useState<string>(defaultSelectedModelPayment);
  const [price, setPrice] = useState<string>(defaultSelectedModelPrice);
  const [selectedMonth, setSelectedMonth] = useState<Month>(DEFAULT_MONTH_OPTION);
  const [interest, setInterest] = useState<string>('2.5');
  const [tilInterest, setTilInterest] = useState<string>('2.5');
  const [tilInterestList, setTilInterestList] = useState<LCVTilInterest[]>([]);
  const [interestType, setInterestType] = useState<InterestType>({
    label: INTEREST_TYPE_TIL,
    value: INTEREST_TYPE_TIL
  });
  const isTilInterestType = interestType.value === INTEREST_TYPE_TIL;

  // Down payment amount to recalculate from on every model/color switch — from the link's
  // `dp`, or the user's last typed value. `null` = none set yet, show this model's default.
  const dpAnchorAmountRef = useRef<number | null>(null);
  const didHandleFirstPathnameRef = useRef(false);
  const previousPathnameRef = useRef(pathname);
  // Ensures the link is resolved exactly once on load, immune to dev-mode re-render replays.
  const hasInitializedFromUrlRef = useRef(false);
  const updateQueryUrl = (params: Record<string, string>) => {
    const searchParams = new URLSearchParams(window.location.search);
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    window.history.replaceState(null, '', `${pathname}?${searchParams}`);
  };

  // Internal versions — used by automatic corrections, never write to the link's `dp`.
  const applyPercentChange = (value: string) => {
    setPayment(computePaymentStringFromPercent(value, price));
    setPercent(value);
  };
  const applyPaymentChange = (value: string) => {
    setPercent(computePercentStringFromPayment(value, price));
    setPayment(value);
  };
  const onChangePercent = (value: string) => {
    dpAnchorAmountRef.current = parseFloat(computePaymentStringFromPercent(value, price));
    applyPercentChange(value);
    updateQueryUrl({
      dp: computePaymentStringFromPercent(value, price)
    });
  };
  const onChangePayment = (value: string) => {
    dpAnchorAmountRef.current = parseFloat(value);
    applyPaymentChange(value);
    updateQueryUrl({
      dp: value
    });
  };
  const onChangeMonth = (month: Month) => {
    setSelectedMonth(month);
    updateQueryUrl({
      term: month.value
    });
  };

  // Resolves the `model` link param to a category/grade/color. `null` = nothing explicit
  // to apply (no param, or it already matches the app default).
  const resolveModelFromParam = (modelParam: string): {
    category: LcvModelCategory;
    subCategory: LcvModelSubCategory | null;
    model: LcvModelWithFieldKey;
    color: LcvModelColor;
  } | null => {
    if (isCalculatorQueryClassParam(modelParam)) {
      if (isQueryModelEqualToDefaultClass(modelParam as ModelCategory, DEFAULT_SELECT_MODEL_CATEGORY, QUERY_MODEL_CATEGORY)) {
        return null;
      }
      const foundedPossibleModelCategories = filterModelCategoriesByUid(modelCategories, QUERY_MODEL_CATEGORY[modelParam as ModelCategory]);
      const category = foundedPossibleModelCategories.length === 1 ? foundedPossibleModelCategories[0] : pickModelCategoryPreferNonEuro4(foundedPossibleModelCategories);
      if (!category) return null;
      const categoryKey = buildCategoryModelKey(category.documentId, modelYear);
      const categoryModelList = allModels[categoryKey] || [];
      const subCategoryList = getModelSubCategoryListFromCategory(getCategoryKey(category), categoryModelList);
      const subCategory = subCategoryList?.[0] ?? null;
      const defaultUid = resolveDefaultGradeUid({
        category,
        subCategory,
        models: categoryModelList,
        defaultGrades: defaultGradesByCategoryKey[categoryKey],
        modelYear
      });
      const resolvedModel = categoryModelList.find(m => m.documentId === defaultUid) ?? categoryModelList[0];
      const resolvedColor = resolvedModel ? getLcvModelDefaultColor(resolvedModel) : null;
      if (!resolvedModel || !resolvedColor) return null;
      return {
        category,
        subCategory,
        model: resolvedModel,
        color: resolvedColor
      };
    }
    const outcome = resolveCalculatorModelQueryWithFallbacks(modelParam, allModels, modelCategories, defaultModelCategory, modelYear, defaultGradesByCategoryKey);
    if (!outcome) return null;
    const categoryKey = buildCategoryModelKey(outcome.category.documentId, modelYear);
    const categoryModelList = allModels[categoryKey] || [];
    const subCategoryList = getModelSubCategoryListFromCategory(getCategoryKey(outcome.category), categoryModelList);
    return {
      category: outcome.category,
      subCategory: getSubCategoryForLcvModel(outcome.resolved.model, subCategoryList),
      model: outcome.resolved.model,
      color: outcome.resolved.color
    };
  };

  // Converts a down payment amount to percent/payment for a given price, clamped between
  // the lender's minimum and the price itself (100%).
  const computeDownPaymentForAnchor = (anchor: number, targetPrice: string, minPercent: number): {
    percent: string;
    payment: string;
  } => {
    const rawPercent = anchor * 100 / parseFloat(targetPrice);
    if (rawPercent < minPercent) {
      return {
        percent: minPercent.toString(),
        payment: computePaymentStringFromPercent(minPercent.toString(), targetPrice)
      };
    }
    if (rawPercent > MAX_PERCENT) {
      return {
        percent: MAX_PERCENT.toString(),
        payment: computePaymentStringFromPercent(MAX_PERCENT.toString(), targetPrice)
      };
    }
    return {
      percent: rawPercent.toFixed(2),
      // anchor is already a whole-baht amount; Math.round is just a safety net.
      payment: Math.round(anchor).toString()
    };
  };
  const setSelectModelAndColor = ({
    modelCategory,
    modelSubCategory,
    selectedModelCategoryListItems: selectedModelCategoryListItemsParam
  }: {
    modelCategory: LcvModelCategory;
    modelSubCategory: LcvModelSubCategory | null;
    selectedModelCategoryListItems?: LcvModelWithFieldKey[] | null;
  }) => {
    const modelList = selectedModelCategoryListItemsParam ?? selectedModelCategoryListItems;
    const categoryKey = buildCategoryModelKey(modelCategory.documentId, modelYear);
    const defaultUID = resolveDefaultGradeUid({
      category: modelCategory,
      subCategory: modelSubCategory,
      models: modelList ?? [],
      defaultGrades: defaultGradesByCategoryKey[categoryKey],
      modelYear
    });
    const selectedModelDefaultItem = modelList?.find(o => o.documentId === defaultUID);
    if (selectedModelDefaultItem) {
      setModel(selectedModelDefaultItem);
      const defaultColor = getLcvModelDefaultColor(selectedModelDefaultItem);
      if (defaultColor) setColor(defaultColor);
      return;
    }
    const fallbackModel = modelList?.[0] || defaultSelectedModel;
    const fallbackColor = getLcvModelDefaultColor(modelList?.[0] ?? defaultSelectedModel) ?? getLcvModelDefaultColor(defaultSelectedModel);
    setModel(fallbackModel);
    if (fallbackColor) setColor(fallbackColor);
  };

  // Resolves car -> down payment -> term from the link, in order, and applies them together.
  const initializeFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const modelParam = searchParams.get('model');
    const dp = searchParams.get('dp');
    const term = searchParams.get('term');

    // 1. model -> category/subCategory/model/color/price
    const resolvedModel = modelParam ? resolveModelFromParam(modelParam) : null;
    const hasExplicitModel = !!resolvedModel;
    const targetCategory = resolvedModel?.category ?? defaultModelCategory;
    const targetSubCategory = resolvedModel?.subCategory ?? defaultModelSubCategory;
    const targetModel = resolvedModel?.model ?? defaultSelectedModel;
    const targetColor = resolvedModel?.color ?? getLcvModelDefaultColor(defaultSelectedModel)!;
    const targetPrice = hasExplicitModel ? ((targetModel.price ?? 0) + (targetColor.price ?? 0)).toString() : defaultSelectedModelPrice;

    // 2. dp -> percent/payment, against the price resolved in step 1
    let targetPercent = defaultPercent;
    let targetPayment = hasExplicitModel ? computePaymentStringFromPercent(defaultPercent, targetPrice) : defaultSelectedModelPayment;
    const hasExplicitDp = !!dp;
    let dpAnchor: number | null = null;
    if (hasExplicitDp) {
      const isNumberDp = Number.isInteger(Math.abs(parseFloat(dp)));
      if (!isNumberDp) {
        // Non-numeric dp — same "not a real choice" bucket as no dp at all.
        targetPercent = defaultPercent;
        targetPayment = computePaymentStringFromPercent(defaultPercent, targetPrice);
      } else if (parseFloat(dp) < 0) {
        // Negative dp — same bucket; fallback display unchanged from today.
        targetPercent = TIL_DEFAULT_PERCENT.toString();
        targetPayment = computePaymentStringFromPercent(TIL_DEFAULT_PERCENT.toString(), targetPrice);
      } else {
        // A real down payment amount — every later switch recalculates from it.
        dpAnchor = parseFloat(dp);
        const tilInterestByModel = getTilInterestByModel(targetCategory, targetSubCategory as LcvModelSubCategory, tilVehicleModel);
        const minPercent = isTilInterestType ? getMinTilDownPaymentPercent(tilInterestByModel) ?? parseFloat(MIN_PERCENT) : parseFloat(MIN_PERCENT);
        const resolved = computeDownPaymentForAnchor(dpAnchor, targetPrice, minPercent);
        targetPercent = resolved.percent;
        targetPayment = resolved.payment;
      }
    }

    // 3. term -> month
    const targetMonth = term ? resolveMonthFromQueryTerm(term, MONTH_OPTIONS, DEFAULT_MONTH_OPTION) : DEFAULT_MONTH_OPTION;

    // 4. apply everything resolved above in one batch
    if (hasExplicitModel) {
      // Hands off the resolved color for the `[model]` effect to apply (see declaration).
      initColorForModelRef.current = {
        modelDocId: targetModel.documentId,
        color: targetColor
      };
      setModelCategory(targetCategory);
      setModelSubCategory(targetSubCategory);
      setModel(targetModel);
      setColor(targetColor);
      setPrice(targetPrice);
    }
    if (hasExplicitModel || hasExplicitDp) {
      setPercent(targetPercent);
      setPayment(targetPayment);
    }
    if (term) {
      setSelectedMonth(targetMonth);
    }
    // Always reset the down payment anchor too, including to `null` for a bare link.
    dpAnchorAmountRef.current = dpAnchor;

    // Marks init as done so the model-sync effect can canonicalize the link.
    isDefaultInit.current = false;
  };
  const resetCalculatorStateToDefaults = () => {
    const defaultColor = getLcvModelDefaultColor(defaultSelectedModel);
    const categoryList = allModels[buildCategoryModelKey(defaultModelCategory.documentId, modelYear)] || [];
    const subCategoryList = getModelSubCategoryListFromCategory(getCategoryKey(defaultModelCategory), categoryList);
    isDefaultInit.current = true;
    lastReconciledModelUidRef.current = defaultSelectedModel.documentId;
    initColorForModelRef.current = null;
    dpAnchorAmountRef.current = null;
    setView('preview');
    setModelCategory(defaultModelCategory);
    setSelectedModelCategoryListItems(categoryList);
    setListModelSubCategory(subCategoryList);
    setModelSubCategory(defaultModelSubCategory);
    setModel(defaultSelectedModel);
    setListColor(getLcvModelColors(defaultSelectedModel));
    if (defaultColor) {
      setColor(defaultColor);
    }
    setPercent(defaultPercent);
    setPayment(defaultSelectedModelPayment);
    setPrice(defaultSelectedModelPrice);
    setSelectedMonth(DEFAULT_MONTH_OPTION);
    setInterest('2.5');
    setTilInterest('2.5');
    setTilInterestList([]);
    setInterestType({
      label: INTEREST_TYPE_TIL,
      value: INTEREST_TYPE_TIL
    });
  };
  const onSubmit = () => {
    if (parseFloatAndHandleNaN(percent) < parseFloat(MIN_PERCENT)) {
      applyPercentChange(MIN_PERCENT);
    }
    if (isTilInterestType) {
      const TIL_INTEREST_BY_MODEL = getTilInterestByModel(modelCategory, modelSubCategory as LcvModelSubCategory, tilVehicleModel);
      const MIN_TIL_PERCENT = getMinTilDownPaymentPercent(TIL_INTEREST_BY_MODEL) ?? parseFloat(MIN_PERCENT);
      if (parseFloatAndHandleNaN(percent) < MIN_TIL_PERCENT) {
        applyPercentChange(MIN_TIL_PERCENT.toString());
      }
      const interest = TIL_INTEREST_BY_MODEL.filter(m => parseFloat(percent) >= m.down_payment_min && parseFloat(percent) <= m.down_payment_max);
      if (interest && interest.length > 0) {
        const mrate = interest[0];
        setTilInterest(mrate[`m${selectedMonth.value}`]);
        setTilInterestList([mrate]);
      }
    }
    setView('result');
    trackLCVCalculator({
      id: modelCategory.title === 'mu-x' ? 'อีซูซุมิว-เอ็กซ์' : 'อีซูซุดีแมคซ์',
      type: modelCategory.nameTh,
      drivetrain: `${model.name} (${parseInt(price).toLocaleString()} บาท)`,
      color: color.color?.nameTh ?? '',
      preferred_price_range: parseFloatAndHandleNaN(percent).toFixed(2),
      interest_rate: parseFloatAndHandleNaN(interest).toFixed(2),
      period: selectedMonth.value
    });
  };

  // Order 1 — page load: resolve car/down payment/term from the link before first paint.
  useLayoutEffect(() => {
    if (hasInitializedFromUrlRef.current) return;
    hasInitializedFromUrlRef.current = true;
    initializeFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allModels, modelCategories, defaultModelCategory, modelYear, defaultGradesByCategoryKey]);

  // Order 2 — user navigates back to this page with a different link: reset and re-resolve.
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = pathname;
    if (!didHandleFirstPathnameRef.current) {
      didHandleFirstPathnameRef.current = true;
      return;
    }
    if (previousPathname === pathname || !pathname?.endsWith('/calculator')) {
      return;
    }
    resetCalculatorStateToDefaults();
    initializeFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Order 3 — user picks a different category: rebuild grade/color lists and pick a default grade.
  useEffect(() => {
    if (!modelCategory || !allModels) return;
    const categoryList = allModels[buildCategoryModelKey(modelCategory.documentId, modelYear)] || [];
    setSelectedModelCategoryListItems(categoryList);
    const subCategoryList = getModelSubCategoryListFromCategory(getCategoryKey(modelCategory), categoryList);
    if (subCategoryList) {
      setListModelSubCategory(subCategoryList);
    } else {
      setListModelSubCategory(null);
    }

    // `model` already belongs to `modelCategory` (replay, or already resolved by the link)
    // — skip re-picking a default grade so it can't clobber what's already correct.
    if (getModelCategoryForLcvModel(model, modelCategories, allModels, modelYear)?.documentId === modelCategory.documentId) {
      return;
    }
    if (subCategoryList) {
      setModelSubCategory(subCategoryList?.[0]);
    } else {
      setModelSubCategory(null);
    }
    setSelectModelAndColor({
      modelCategory,
      modelSubCategory: subCategoryList?.[0] as LcvModelSubCategory,
      selectedModelCategoryListItems: categoryList
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelCategory]);

  // Order 4 — page load or model change: resolve the color to show.
  useEffect(() => {
    setListColor(getLcvModelColors(model));

    // Checked non-destructively — must not clear on mismatch (see calculator-skills.md §7).
    const pendingColor = initColorForModelRef.current;
    if (pendingColor && pendingColor.modelDocId === model.documentId) {
      setColor(pendingColor.color);
      lastReconciledModelUidRef.current = model.documentId;
      return;
    }

    // Same-id reference swap: skip re-deriving color.
    if (lastReconciledModelUidRef.current === model.documentId) return;
    lastReconciledModelUidRef.current = model.documentId;
    const defaultColor = getLcvModelDefaultColor(model);
    if (defaultColor) setColor(defaultColor);
  }, [model]);

  // Order 5 — model or color changes: recompute price.
  useEffect(() => {
    if (!color || !model || isDefaultInit.current && model.documentId === defaultSelectedModel.documentId) return;
    const priceString = ((model.price ?? 0) + (color.price ?? 0)).toString();
    setPrice(priceString);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, color]);

  // Order 6 — model or color changes: sync `model` (not dp/term) to the link.
  useEffect(() => {
    if (!color) return;
    if (!model) return;
    updateQueryUrl({
      model: createModelQueryParam(model.fieldKey ?? model.documentId, getLcvModelColorId(color))
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, color]);

  // Order 7 — price or financing option changes: recalculate the down payment
  // (see calculator-skills.md §6).
  useEffect(() => {
    if (isDefaultInit.current && model.documentId === defaultSelectedModel.documentId) return;
    if (dpAnchorAmountRef.current === null) {
      // No down payment set — show this model's own default.
      applyPercentChange(defaultPercent);
      return;
    }
    const minPercent = isTilInterestType ? getMinTilDownPaymentPercent(getTilInterestByModel(modelCategory, modelSubCategory as LcvModelSubCategory, tilVehicleModel)) ?? parseFloat(MIN_PERCENT) : parseFloat(MIN_PERCENT);
    const {
      percent,
      payment
    } = computeDownPaymentForAnchor(dpAnchorAmountRef.current, price, minPercent);
    setPercent(percent);
    setPayment(payment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, modelCategory, modelSubCategory, interestType]);

  // Order 8 — user submits: scroll to the top of the result.
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  }, [view]);

  // Order 9 — category/financing option changes: enforce the lender's minimum down payment.
  useEffect(() => {
    if (isTilInterestType) {
      const TIL_INTEREST_BY_MODEL = getTilInterestByModel(modelCategory, modelSubCategory as LcvModelSubCategory, tilVehicleModel);
      const MIN_TIL_PERCENT = getMinTilDownPaymentPercent(TIL_INTEREST_BY_MODEL) ?? parseFloat(MIN_PERCENT);
      if (parseFloatAndHandleNaN(percent) < MIN_TIL_PERCENT) {
        applyPercentChange(MIN_TIL_PERCENT.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelCategory, modelSubCategory, interestType]);
  const onSelectModelSubCategory = (modelSubCategory: LcvModelSubCategory) => {
    setModelSubCategory(modelSubCategory);
    setSelectModelAndColor({
      modelCategory,
      modelSubCategory
    });
  };
  return {
    view,
    setView,
    modelCategory,
    setModelCategory,
    listModelSubCategory,
    modelSubCategory,
    onSelectModelSubCategory,
    selectedModelCategoryListItems,
    model,
    setModel,
    listColor,
    color,
    setColor,
    percent,
    onChangePercent,
    payment,
    onChangePayment,
    price,
    selectedMonth,
    onChangeMonth,
    interest,
    setInterest,
    tilInterest,
    tilInterestList,
    interestType,
    setInterestType,
    isTilInterestType,
    onSubmit
  };
};